import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Select,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Progress,
  Icon,
  Flex,
} from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { useFindMany, useGlobalAction, useFetch } from "@gadgetinc/react";
import { useDropzone } from "react-dropzone";
import { api } from "../api";
import { FiUpload, FiFile, FiDownload, FiEye } from "react-icons/fi";

export default function DocumentProcessing() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // State management
  const [selectedConfigId, setSelectedConfigId] = useState("");
  const [processingMode, setProcessingMode] = useState("upload"); // "upload" or "manual"
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [manualData, setManualData] = useState({});
  const [processingResults, setProcessingResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);

  // API hooks
  const [{ data: configs, fetching: configsFetching, error: configsError }] = useFindMany(
    api.documentProcessorConfig,
    {
      select: {
        id: true,
        name: true,
        description: { markdown: true },
        processingGoal: { markdown: true },
        detectedFields: true,
        institution: true,
        isActive: true,
        fieldDefinitionMethod: true,
        inputMethod: true,
        accessLevel: true,
      },
      filter: { isActive: { equals: true } },
      sort: { name: "Ascending" },
    }
  );

  const [{ data: processResult, fetching: processing, error: processError }, processDocument] = 
    useGlobalAction(api.processDocument);

  // Auto-select configuration if only one available
  useEffect(() => {
    if (configs && configs.length === 1 && !selectedConfigId) {
      setSelectedConfigId(configs[0].id);
    }
  }, [configs, selectedConfigId]);

  // File drop zone configuration
  const onDrop = useCallback((acceptedFiles) => {
    setUploadedFiles(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp'],
      'text/csv': ['.csv'],
      'application/json': ['.json'],
    },
    maxFiles: 1,
  });

  // Get selected configuration
  const selectedConfig = configs?.find(config => config.id === selectedConfigId);

  // Handle processing
  const handleProcess = async () => {
    if (!selectedConfigId) {
      toast({
        title: "Configuration Required",
        description: "Please select a processor configuration first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      let result;
      if (processingMode === "upload" && uploadedFiles.length > 0) {
        // Handle file upload processing
        const formData = new FormData();
        formData.append('file', uploadedFiles[0]);
        formData.append('configId', selectedConfigId);
        
        result = await processDocument({
          configId: selectedConfigId,
          fileData: uploadedFiles[0],
          processingMode: "file_upload"
        });
      } else if (processingMode === "manual" && Object.keys(manualData).length > 0) {
        // Handle manual data processing
        result = await processDocument({
          configId: selectedConfigId,
          manualData: manualData,
          processingMode: "manual_entry"
        });
      } else {
        toast({
          title: "Input Required",
          description: processingMode === "upload" 
            ? "Please upload a file to process." 
            : "Please enter data in the form fields.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (result) {
        setProcessingResults(prev => [result, ...prev]);
        toast({
          title: "Processing Complete",
          description: "Document has been processed successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Processing Failed",
        description: error.message || "An error occurred during processing.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle manual data input
  const handleManualDataChange = (field, value) => {
    setManualData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Export results
  const exportResults = (result, format = 'json') => {
    const data = format === 'json' 
      ? JSON.stringify(result, null, 2)
      : convertToCSV(result);
    
    const blob = new Blob([data], { 
      type: format === 'json' ? 'application/json' : 'text/csv' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `processing-result-${Date.now()}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Convert to CSV (simplified)
  const convertToCSV = (data) => {
    if (!data || typeof data !== 'object') return '';
    const headers = Object.keys(data);
    const values = Object.values(data);
    return [headers.join(','), values.join(',')].join('\n');
  };

  // Loading state
  if (configsFetching) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="center">
          <Spinner size="xl" />
          <Text>Loading processor configurations...</Text>
        </VStack>
      </Container>
    );
  }

  // No configurations available
  if (!configs || configs.length === 0) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="center">
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>No Processor Configurations Found</AlertTitle>
              <AlertDescription>
                You need to create a document processor configuration before you can process documents.
              </AlertDescription>
            </Box>
          </Alert>
          <Button as={Link} to="/document-processor-configurator" colorScheme="blue" size="lg">
            Create Processor Configuration
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header Section */}
        <Box>
          <HStack justify="space-between" mb={4}>
            <Box>
              <Heading size="lg" mb={2}>Document Processing</Heading>
              <Text color="gray.600">
                Process documents using AI-powered extraction and analysis tools.
                Upload files or enter data manually for processing.
              </Text>
            </Box>
            <Button as={Link} to="/document-processor-configurator" colorScheme="blue" variant="outline">
              Create New Processor
            </Button>
          </HStack>
        </Box>

        {/* Configuration Selection */}
        <Card>
          <CardHeader>
            <Heading size="md">Processor Configuration</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {configs.length > 1 && (
                <FormControl>
                  <FormLabel>Select Processor Configuration</FormLabel>
                  <Select
                    placeholder="Choose a configuration..."
                    value={selectedConfigId}
                    onChange={(e) => setSelectedConfigId(e.target.value)}
                  >
                    {configs.map((config) => (
                      <option key={config.id} value={config.id}>
                        {config.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              {selectedConfig && (
                <Box p={4} bg="gray.50" borderRadius="md">
                  <VStack spacing={2} align="start">
                    <HStack>
                      <Text fontWeight="bold">Selected:</Text>
                      <Text>{selectedConfig.name}</Text>
                      <Badge colorScheme="green">{selectedConfig.accessLevel}</Badge>
                    </HStack>
                    {selectedConfig.description && (
                      <Text fontSize="sm" color="gray.600">
                        {selectedConfig.description.markdown}
                      </Text>
                    )}
                    {selectedConfig.processingGoal && (
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold">Processing Goal:</Text>
                        <Text fontSize="sm">{selectedConfig.processingGoal.markdown}</Text>
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Processing Interface */}
        {selectedConfig && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">Processing Interface</Heading>
                <HStack>
                  <Button
                    variant={processingMode === "upload" ? "solid" : "outline"}
                    colorScheme="blue"
                    size="sm"
                    onClick={() => setProcessingMode("upload")}
                  >
                    File Upload
                  </Button>
                  <Button
                    variant={processingMode === "manual" ? "solid" : "outline"}
                    colorScheme="blue"
                    size="sm"
                    onClick={() => setProcessingMode("manual")}
                  >
                    Manual Entry
                  </Button>
                </HStack>
              </HStack>
            </CardHeader>
            <CardBody>
              {processingMode === "upload" ? (
                <VStack spacing={6}>
                  {/* File Upload Dropzone */}
                  <Box
                    {...getRootProps()}
                    border="2px dashed"
                    borderColor={isDragActive ? "blue.400" : "gray.300"}
                    borderRadius="md"
                    p={8}
                    textAlign="center"
                    cursor="pointer"
                    bg={isDragActive ? "blue.50" : "gray.50"}
                    transition="all 0.2s"
                    w="100%"
                  >
                    <input {...getInputProps()} />
                    <VStack spacing={4}>
                      <Icon as={FiUpload} boxSize={12} color="gray.400" />
                      {isDragActive ? (
                        <Text>Drop the file here...</Text>
                      ) : (
                        <VStack spacing={2}>
                          <Text fontWeight="semibold">Drag & drop a file here, or click to select</Text>
                          <Text fontSize="sm" color="gray.500">
                            Supports PDF, DOCX, Images, CSV, JSON files
                          </Text>
                        </VStack>
                      )}
                    </VStack>
                  </Box>

                  {/* Uploaded File Preview */}
                  {uploadedFiles.length > 0 && (
                    <Box w="100%" p={4} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                      <HStack>
                        <Icon as={FiFile} color="green.500" />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="semibold">{uploadedFiles[0].name}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {(uploadedFiles[0].size / 1024 / 1024).toFixed(2)} MB
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  )}

                  {/* Process Button */}
                  <Button
                    colorScheme="blue"
                    size="lg"
                    onClick={handleProcess}
                    isLoading={processing}
                    loadingText="Processing..."
                    isDisabled={uploadedFiles.length === 0}
                    w="100%"
                  >
                    Process Document
                  </Button>
                </VStack>
              ) : (
                <VStack spacing={6}>
                  {/* Manual Data Entry Form */}
                  <Text>Enter data manually for processing:</Text>
                  
                  {selectedConfig.detectedFields && Object.keys(selectedConfig.detectedFields).map((field) => (
                    <FormControl key={field}>
                      <FormLabel>{field.charAt(0).toUpperCase() + field.slice(1)}</FormLabel>
                      <Textarea
                        placeholder={`Enter ${field}...`}
                        value={manualData[field] || ""}
                        onChange={(e) => handleManualDataChange(field, e.target.value)}
                      />
                    </FormControl>
                  ))}

                  {/* Generic fields if no detected fields */}
                  {(!selectedConfig.detectedFields || Object.keys(selectedConfig.detectedFields).length === 0) && (
                    <>
                      <FormControl>
                        <FormLabel>Data Content</FormLabel>
                        <Textarea
                          placeholder="Enter your data content here..."
                          value={manualData.content || ""}
                          onChange={(e) => handleManualDataChange("content", e.target.value)}
                          rows={6}
                        />
                      </FormControl>
                    </>
                  )}

                  {/* Process Button */}
                  <Button
                    colorScheme="blue"
                    size="lg"
                    onClick={handleProcess}
                    isLoading={processing}
                    loadingText="Processing..."
                    isDisabled={Object.keys(manualData).length === 0}
                    w="100%"
                  >
                    Process Data
                  </Button>
                </VStack>
              )}

              {/* Processing Progress */}
              {processing && (
                <Box mt={6}>
                  <Progress isIndeterminate colorScheme="blue" borderRadius="md" />
                  <Text textAlign="center" mt={2} fontSize="sm">
                    Processing your document...
                  </Text>
                </Box>
              )}
            </CardBody>
          </Card>
        )}

        {/* Results Display */}
        {processingResults.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Processing Results</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {processingResults.map((result, index) => (
                  <Box key={index} p={4} bg="gray.50" borderRadius="md">
                    <HStack justify="space-between" mb={3}>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="semibold">Result #{processingResults.length - index}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {new Date().toLocaleString()}
                        </Text>
                      </VStack>
                      <HStack>
                        <Button
                          size="sm"
                          leftIcon={<FiEye />}
                          onClick={() => {
                            setSelectedResult(result);
                            onOpen();
                          }}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          leftIcon={<FiDownload />}
                          onClick={() => exportResults(result, 'json')}
                        >
                          JSON
                        </Button>
                        <Button
                          size="sm"
                          leftIcon={<FiDownload />}
                          onClick={() => exportResults(result, 'csv')}
                        >
                          CSV
                        </Button>
                      </HStack>
                    </HStack>
                    
                    {result.success && (
                      <Badge colorScheme="green" mb={2}>Success</Badge>
                    )}
                    
                    <Text fontSize="sm" noOfLines={3}>
                      {JSON.stringify(result.result || result, null, 2)}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Error Display */}
        {(configsError || processError) && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {configsError?.message || processError?.message || "An unexpected error occurred."}
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </VStack>

      {/* Result Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Processing Result Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedResult && (
              <Box>
                <pre style={{ 
                  background: '#f7fafc', 
                  padding: '1rem', 
                  borderRadius: '0.375rem',
                  overflow: 'auto',
                  maxHeight: '60vh',
                  fontSize: '0.875rem'
                }}>
                  {JSON.stringify(selectedResult, null, 2)}
                </pre>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              leftIcon={<FiDownload />}
              onClick={() => selectedResult && exportResults(selectedResult, 'json')}
            >
              Export JSON
            </Button>
            <Button
              mr={3}
              leftIcon={<FiDownload />}
              onClick={() => selectedResult && exportResults(selectedResult, 'csv')}
            >
              Export CSV
            </Button>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}